import { Link } from "../lib/route";
import { ArrowRight, CheckCircle2 } from "lucide-react";
import Hero from "../components/Hero";
import CTABanner from "../components/CTABanner";
import FAQSection from "../components/FAQSection";
import { SERVICES, IMAGES, COPY } from "../lib/_data";

export default function Services() {
  const hero = COPY.services.hero;
  const finalCta = COPY.services.finalCta;
  const detail = COPY.services.detail || {};
  return (
    <div data-testid="page-services">
      <Hero
        image={IMAGES.servicesHero}
        eyebrow={hero.eyebrow || "Full-service local team"}
        title={hero.title || "Everything your home needs, done right."}
        subtitle={hero.subtitle || "Licensed technicians, transparent pricing, and a written workmanship warranty on every service — from a quick fix to a full remodel."}
        primaryCta={{ label: hero.primaryCtaLabel || "Request a Quote", to: hero.primaryCtaTo || "/consultations" }}
        secondaryCta={{ label: hero.secondaryCtaLabel || "Contact Us", to: hero.secondaryCtaTo || "/contact" }}
      />

      {/* Detailed service list — alternating layout, big and airy */}
      <section className="bg-[#f9f6f0] py-20 md:py-28">
        <div className="max-w-7xl mx-auto px-6 md:px-10 lg:px-14 space-y-20 md:space-y-28">
          {SERVICES.map((s, i) => (
            <div
              key={s.slug}
              id={s.slug}
              data-testid={`service-detail-${s.slug}`}
              className={`grid grid-cols-1 lg:grid-cols-12 gap-8 lg:gap-16 items-center ${
                i % 2 === 1 ? "lg:[&>*:first-child]:order-2" : ""
              }`}
            >
              <div className="lg:col-span-6 relative rounded-3xl overflow-hidden aspect-[5/4] border border-[#e5e0d8] bg-gradient-to-br from-[#e5e0d8] via-[#f5efe4] to-[#c85a3c]/20">
                {s.image ? (
                  <img
                    src={s.image}
                    alt={s.title}
                    className="w-full h-full object-cover"
                    loading="lazy"
                  />
                ) : (
                  <div className="absolute inset-0 flex items-center justify-center">
                    <span className="font-display text-6xl md:text-7xl font-black text-[#c85a3c]/70 tracking-tight">
                      {(s.title || "?").slice(0, 2).toUpperCase()}
                    </span>
                  </div>
                )}
                <div className="absolute inset-0 bg-gradient-to-t from-[#2c2a29]/40 to-transparent" />
                <div className="absolute top-6 left-6 text-[10px] tracking-[0.24em] uppercase font-bold bg-[#fffdf9] text-[#2c2a29] px-3 py-1.5 rounded-full">
                  0{i + 1} / Service
                </div>
              </div>

              <div className="lg:col-span-6">
                <h2 className="font-display text-3xl md:text-5xl font-black text-[#2c2a29] leading-[1.05]">
                  {s.title}
                </h2>
                <p className="mt-5 text-[#5c5855] leading-relaxed text-lg">
                  {s.long}
                </p>
                <ul className="mt-8 grid grid-cols-1 sm:grid-cols-2 gap-3">
                  {s.bullets.map((b) => (
                    <li key={b} className="flex items-start gap-3">
                      <CheckCircle2 className="w-5 h-5 text-[#849982] mt-0.5 flex-shrink-0" />
                      <span className="text-[#2c2a29] font-medium text-sm">
                        {b}
                      </span>
                    </li>
                  ))}
                </ul>
                <div className="mt-8 flex flex-col sm:flex-row gap-3">
                  <Link
                    to={detail.quoteTo || "/consultations"}
                    data-testid={`service-detail-quote-${s.slug}`}
                    className="inline-flex items-center justify-center gap-2 bg-[#c85a3c] hover:bg-[#a64b32] text-[#fffdf9] font-bold px-6 py-3.5 rounded-full transition-all hover:-translate-y-0.5"
                  >
                    {detail.quoteLabel || "Request Quote"} <ArrowRight className="w-4 h-4" />
                  </Link>
                  <Link
                    to={detail.bookTo || "/consultations"}
                    data-testid={`service-detail-book-${s.slug}`}
                    className="inline-flex items-center justify-center gap-2 border-2 border-[#2c2a29] text-[#2c2a29] hover:bg-[#2c2a29] hover:text-[#fffdf9] font-bold px-6 py-3.5 rounded-full transition-colors"
                  >
                    {detail.bookLabel || "Schedule Service"}
                  </Link>
                </div>
              </div>
            </div>
          ))}
        </div>
      </section>

      <FAQSection subset={6} />

      <CTABanner
        image={IMAGES.finalCta}
        eyebrow={finalCta.eyebrow || "Ready when you are"}
        title={finalCta.title || "Every project starts with a conversation."}
        subtitle={finalCta.subtitle || "Not sure which service fits? Book a free consultation and we'll help you figure it out."}
        primary={{ label: finalCta.primaryLabel || "Book a Consultation", to: finalCta.primaryTo || "/consultations" }}
        secondary={{ label: finalCta.secondaryLabel || "Call Now", to: finalCta.secondaryTo || "/contact" }}
      />
    </div>
  );
}
